/*
   The window.fetch wrapper: Slack DM, reply and reaction telemetry, and the one retry the browser
   is allowed to make on its own.

   A classic script in the core chain (CORE_SCRIPTS in lib/layout.js), enqueued straight after
   core.js and inbox-badge.js. That is the position this code held at the end of core.js, so fetch
   is wrapped before any later script calls it at load, and trackEvent (core.js) exists by the time
   the first request goes out. scripts/checkFrontendRetry.js loads the wrapper out of this file by
   its IIFE name, and the extension's toolbar popup takes it with the rest of the core chain
   (extension/build.js).
*/

// Monkey-patch window.fetch to automatically capture asynchronous Slack DMs, replies, and reactions,
// and to retry the one failure a request can have without ever reaching the app.
//
// ── WHY A 503 THAT ARRIVES AS HTML IS SAFE TO SEND AGAIN, AND NO OTHER FAILURE IS ─────────────────
// On roughly 1 deploy in 3, Cloud Run's own serving frontend answers "Error: Service Unavailable /
// Service is disabled" for between 1 and 30 seconds while traffic moves to the new revision. None of
// it reaches the container: across every burst measured on 2026-09-09 and 2026-09-11 the app logged
// no 5xx at all, and the load balancer recorded each one as sent by the backend. The instance floor,
// the startup probe and the front door were all ruled out; nothing in how the service is deployed
// decides which switches do it.
//
// The load balancer already retries a GET or HEAD past it. It cannot retry a write, because it
// cannot tell that 503 from one the app sent, and sending a save or a message again after the app
// acted on it would do it twice. The browser CAN tell: every response this app sends from /api is a
// JSON envelope, including its own 503s, and Google's page is text/html. So an HTML 503 from our own
// /api is proof the request never got as far as the app, and repeating it cannot repeat anything.
//
// Everything else is left exactly as it was: a JSON 503, any other status, another origin, a path
// outside /api, and a body that can only be read once (a stream).
(function instrumentFetchTelemetry() {
  var originalFetch = window.fetch;

  var FRONTEND_503_RETRIES = 2;

  function sentByFrontendNotApp(r) {
    if (!r || r.status !== 503) return false;
    var type = (r.headers && r.headers.get && r.headers.get('content-type')) || '';
    return type.toLowerCase().indexOf('text/html') !== -1;
  }

  function isOwnApi(urlStr) {
    try {
      var u = new URL(urlStr, window.location.href);
      return u.origin === window.location.origin && u.pathname.indexOf('/api/') === 0;
    } catch (_) {
      return false; // an unparseable address is not one of ours, so it is not retried
    }
  }

  // A string, form data, URL parameters, a Blob or a buffer can be sent again as-is. A stream is
  // consumed by the first send, and there is nothing left to repeat.
  function bodyCanBeResent(body) {
    if (body === undefined || body === null) return true;
    return !(typeof window.ReadableStream !== 'undefined' && body instanceof window.ReadableStream);
  }

  // ── THE BEACON IS NEVER RETRIED, AND THAT IS NOT AN OPTIMIZATION ────────────────────────────────
  // trackEvent() posts to /api/auth/metrics/event through this very wrapper, so retrying it closed
  // a loop on itself: a frontend 503 burst made every retried request emit a beacon, each beacon
  // was itself retryable, and each retry of a beacon emitted another beacon. Across the 2 bursts
  // measured in September the beacon was the single largest source of 503s anywhere on the site
  // (17 of 38, then 19 of 26), which is an absurd thing for a fire-and-forget ping to be. It is
  // not free on the server either: every ping costs an identity lookup, a manager lookup and 3
  // Firestore writes, so the storm was paying all of that to report that something had failed.
  //
  // A lost telemetry ping costs nothing at all, so the beacon simply does not qualify for a retry.
  var TELEMETRY_PATH = '/api/auth/metrics/event';

  function isTelemetryBeacon(urlStr) {
    try {
      return new URL(urlStr, window.location.href).pathname === TELEMETRY_PATH;
    } catch (_) {
      return false; // an unparseable address is not the beacon, and is judged on its own merits
    }
  }

  // Breaking the loop is not enough by itself. One report per retry per request still floods during
  // a burst — 20 requests in flight, each retried twice, is 40 pings in a few seconds, all saying
  // the same thing about the same outage. The first few carry everything the rest would, so a page
  // reports a bounded number and then stays quiet. The count resets per page, which is the right
  // granularity: a burst does not outlive one.
  var RETRY_REPORTS_PER_PAGE = 3;
  var retryReports = 0;

  window.fetch = function(url, options) {
    var self = this;
    var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
    var isRequest = typeof window.Request !== 'undefined' && url instanceof window.Request;
    // A Request carries its own body and is always re-sendable through clone(), which tees even a
    // stream; only a bare stream handed in as options.body is gone after the first send.
    var retryable = isOwnApi(urlStr) && !isTelemetryBeacon(urlStr) && bodyCanBeResent(options && options.body);
    // A Request's body can be read once, so keep an untouched copy from before the first send and
    // clone from THAT each time; cloning the one already sent would clone an empty body.
    var spare = retryable && isRequest ? url.clone() : null;

    if (options && options.method === 'POST') {
      try {
        var body = null;
        if (typeof options.body === 'string') {
          body = JSON.parse(options.body);
        }
        if (urlStr.indexOf('/api/slack/dm') !== -1) {
          trackEvent('dm_sent');
        } else if (urlStr.indexOf('/api/slack/reaction') !== -1) {
          if (body && body.emojiName) {
            trackEvent('reaction_clicked', { emoji: body.emojiName });
          }
        } else if (urlStr.indexOf('/api/slack/proxy') !== -1 && body && body.endpoint === 'chat.postMessage') {
          if (body.thread_ts) {
            trackEvent('reply_posted');
          } else {
            trackEvent('dm_sent');
          }
        }
      } catch (err) {
        // Silent catch to prevent telemetry parsing errors from breaking app logic
      }
    }

    function attempt(n, req) {
      return originalFetch.call(self, req, options).then(function(r) {
        if (!retryable || n >= FRONTEND_503_RETRIES || !sentByFrontendNotApp(r)) return r;
        if (retryReports < RETRY_REPORTS_PER_PAGE) {
          retryReports++;
          try {
            trackEvent('frontend_503_retried', { path: new URL(urlStr, window.location.href).pathname, attempt: n + 1 });
          } catch (_) { /* telemetry must never stand between a reader and the retry */ }
        }
        // Short and growing: the burst is per frontend and a retry usually lands on a healthy one.
        return new Promise(function(resolve) { setTimeout(resolve, 300 * (n + 1)); })
          .then(function() { return attempt(n + 1, spare ? spare.clone() : req); });
      });
    }
    return attempt(0, spare ? spare.clone() : url);
  };
})();
